import { router } from "@inertiajs/react"
import { TicketStatusBadge } from "@/components/domain/ticket-status-badge"
import { TicketPriorityBadge } from "@/components/domain/ticket-priority-badge"
import { SlaPill } from "./sla-pill"
import { fmtRelativeTime } from "@/lib/format"
import type { Ticket } from "@/types/ticket"

type Props = {
  tickets: Ticket[]
}

// Mobile / narrow-viewport card list. Renders the same `Ticket[]` data
// the desktop <TicketsTable> consumes, but stacks the priority-1 fields
// (ID / subject / requester / status + priority badges / SLA pills) into
// a tap-target card. Hidden at `lg:` and up — the desktop layout uses
// the full table.
//
// Click anywhere on the card to open the ticket detail.
export function TicketsCards({ tickets }: Props) {
  if (tickets.length === 0) {
    return (
      <div className="rounded-card border border-border bg-app-panel p-6 text-center text-sm text-app-muted">
        No tickets match these filters.
      </div>
    )
  }

  return (
    <ul className="space-y-3">
      {tickets.map((t) => (
        <li key={t.id}>
          <button
            type="button"
            onClick={() => router.visit(`/tickets/${t.id}`)}
            className="block w-full rounded-card border border-border bg-app-panel p-4 text-left transition active:bg-white/[0.04]"
          >
            {/* Top row — ID + status + priority. */}
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-medium text-app-text">{ticketNumberLabel(t)}</span>
              <TicketStatusBadge status={t.status} />
              <TicketPriorityBadge priority={t.priority} />
              {t.lastMessageAt && (
                <span className="ml-auto text-xs text-app-muted" title={t.lastMessageAt}>
                  {fmtRelativeTime(t.lastMessageAt)}
                </span>
              )}
            </div>

            {/* Subject — wraps to two lines max. */}
            <div className="mt-2 line-clamp-2 text-base text-app-text">
              {t.subject}
            </div>

            {/* Requester — name + email below. */}
            <div className="mt-2 text-sm">
              <div className="text-app-text">{t.requester.name || "—"}</div>
              {t.requester.email && (
                <div className="truncate text-xs text-app-muted">{t.requester.email}</div>
              )}
            </div>

            {/* SLA pills row — only render the ones that are tracked.
                Three rows compresses the SLA cell into a single horizontal
                strip on the card. */}
            <div className="mt-3 flex flex-wrap gap-2">
              {t.sla.firstResponse.state !== "not_tracked" && (
                <SlaPill
                  label="FR"
                  state={t.sla.firstResponse.state}
                  dueAt={t.sla.firstResponse.dueAt}
                  metAt={t.sla.firstResponse.metAt}
                  breachedAt={t.sla.firstResponse.breachedAt}
                />
              )}
              {t.sla.idleResponse.state !== "not_tracked" && (
                <SlaPill
                  label="Idle"
                  state={t.sla.idleResponse.state}
                  dueAt={t.sla.idleResponse.dueAt}
                  breachedAt={t.sla.idleResponse.firstBreachedAt}
                  breachCount={t.sla.idleResponse.breachCount}
                />
              )}
              {t.sla.resolution.state !== "not_tracked" && (
                <SlaPill
                  label="Res"
                  state={t.sla.resolution.state}
                  dueAt={t.sla.resolution.dueAt}
                  breachedAt={t.sla.resolution.breachedAt}
                />
              )}
            </div>
          </button>
        </li>
      ))}
    </ul>
  )
}

function ticketNumberLabel(t: Ticket) {
  if (t.displayTicketNumber) return t.displayTicketNumber
  if (t.supportProvider === "zendesk" && t.externalId) return `ZD-${t.externalId}`
  return t.displayTicketNumber || (t.ticketNumber != null ? `TK-${t.ticketNumber}` : "—")
}
